import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, Trash2, Pencil, X } from "lucide-react";

import { api } from "../lib/api";

import { Card } from "./ui/card";
import { Button } from "./ui/button";

const EMPTY = {
  consultation_id: "",
  patient_age_band: "",
  patient_city: "",
  patient_gender: "",
  condition: "",
  presenting_complaint: "",
  diagnosis_change: "",
  outcome_summary: "",
  doctor_name: "",
  doctor_specialization: "",
  months_ago: 1,
  is_published: true,
  order_priority: 100,
};

/** Admin curation widget for Public Case Outcomes (landing-page social proof). */
export default function CaseOutcomesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/case-outcomes");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load case outcomes");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY); setEditingId(null); setShowForm(false); };

  const submit = async () => {
    if (!form.condition.trim() || !form.outcome_summary.trim()) {
      toast.error("Condition and Outcome are required"); return;
    }
    if (!form.patient_age_band.trim() || !form.patient_city.trim()) {
      toast.error("Age band and City are required"); return;
    }
    if (!form.doctor_name.trim()) {
      toast.error("Doctor name is required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, months_ago: Number(form.months_ago) || 0, order_priority: Number(form.order_priority) || 100 };
      if (editingId) {
        await api.patch(`/admin/case-outcomes/${editingId}`, payload);
        toast.success("Outcome updated");
      } else {
        await api.post("/admin/case-outcomes", payload);
        toast.success("Outcome published");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const togglePublish = async (o) => {
    try {
      await api.patch(`/admin/case-outcomes/${o.id}`, { is_published: !o.is_published });
      toast.success(o.is_published ? "Hidden from landing" : "Published to landing");
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete outcome "${o.condition}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/case-outcomes/${o.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (o) => {
    setForm({ ...EMPTY, ...o });
    setEditingId(o.id);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  return (
    <div data-testid="admin-case-outcomes">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl font-semibold text-slate-900">Public Case Outcomes</h3>
          <p className="text-sm text-slate-500">Anonymised stories that appear on the landing page.</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }} data-testid="toggle-outcome-form" className="bg-blue-700 hover:bg-blue-800">
          {showForm ? <><X className="w-4 h-4 mr-1"/> Cancel</> : <><Plus className="w-4 h-4 mr-1"/> Add outcome</>}
        </Button>
      </div>

      {showForm && (
        <Card className="p-5 mb-6 border-blue-200 bg-blue-50/30" data-testid="outcome-form">
          <h4 className="font-semibold text-slate-900 mb-3">{editingId ? "Edit outcome" : "New outcome"}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Field label="Age band *" placeholder="e.g. 40-50" value={form.patient_age_band} onChange={(v) => set("patient_age_band", v)} testId="oc-age"/>
            <Field label="City *" placeholder="e.g. Hyderabad" value={form.patient_city} onChange={(v) => set("patient_city", v)} testId="oc-city"/>
            <Field label="Gender (optional)" placeholder="Male / Female / —" value={form.patient_gender} onChange={(v) => set("patient_gender", v)} testId="oc-gender"/>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 text-sm">
            <Field label="Condition / Headline *" placeholder="e.g. Recurring migraines misdiagnosed as epilepsy" value={form.condition} onChange={(v) => set("condition", v)} testId="oc-condition"/>
            <Field label="Presented with (anonymized)" placeholder="e.g. 6-month history of throbbing one-sided headache + nausea" value={form.presenting_complaint} onChange={(v) => set("presenting_complaint", v)} testId="oc-complaint" textarea/>
            <Field label="Diagnosis update" placeholder="e.g. Previously: epilepsy on long-term anti-seizure meds → Updated: migraine with aura" value={form.diagnosis_change} onChange={(v) => set("diagnosis_change", v)} testId="oc-diagnosis" textarea/>
            <Field label="Outcome *" placeholder="e.g. Headache-free in 4 weeks with adjusted medication" value={form.outcome_summary} onChange={(v) => set("outcome_summary", v)} testId="oc-outcome" textarea/>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Field label="Doctor *" placeholder="Dr. Sathya Reddy Kalahasti" value={form.doctor_name} onChange={(v) => set("doctor_name", v)} testId="oc-doctor"/>
            <Field label="Specialization" placeholder="Neurologist" value={form.doctor_specialization} onChange={(v) => set("doctor_specialization", v)} testId="oc-spec"/>
            <Field label="Months ago" type="number" value={form.months_ago} onChange={(v) => set("months_ago", v)} testId="oc-months"/>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-end">
            <Field label="Display order (lower = first)" type="number" value={form.order_priority} onChange={(v) => set("order_priority", v)} testId="oc-order"/>
            <Field label="Consultation ref (optional, hidden)" placeholder="Case id for internal tracking" value={form.consultation_id} onChange={(v) => set("consultation_id", v)} testId="oc-cref"/>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mt-5">
              <input type="checkbox" checked={!!form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="oc-published"/>
              Publish immediately
            </label>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={submit} disabled={busy} data-testid="oc-save" className="bg-blue-700 hover:bg-blue-800">
              {busy ? "Saving…" : editingId ? "Update outcome" : "Publish outcome"}
            </Button>
            <Button variant="outline" onClick={resetForm} data-testid="oc-cancel">Cancel</Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((o) => (
          <Card key={o.id} className="p-4 border-slate-200" data-testid={`outcome-row-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                  <span>{o.patient_age_band}{o.patient_gender ? ` · ${o.patient_gender}` : ""}</span>
                  <span className="w-1 h-1 rounded-full bg-slate-300"/>
                  <span>{o.patient_city}</span>
                </div>
                <div className="font-semibold text-slate-900 mt-1 truncate" title={o.condition}>{o.condition}</div>
                <div className="text-xs text-slate-600 mt-1 line-clamp-2">{o.outcome_summary}</div>
                <div className="text-[10px] text-slate-400 mt-1">{o.doctor_name} · {o.months_ago === 0 ? "this month" : o.months_ago === 1 ? "1 mo ago" : `${o.months_ago} mo ago`}</div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${o.is_published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {o.is_published ? "PUBLIC" : "HIDDEN"}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={() => startEdit(o)} className="h-7 px-2.5 text-[11px]" data-testid={`outcome-edit-${o.id}`}>
                <Pencil className="w-3 h-3 mr-1"/> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => togglePublish(o)} className="h-7 px-2.5 text-[11px]" data-testid={`outcome-toggle-${o.id}`}>
                {o.is_published ? <><EyeOff className="w-3 h-3 mr-1"/> Hide</> : <><Eye className="w-3 h-3 mr-1"/> Publish</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(o)} className="h-7 px-2.5 text-[11px] text-red-700 border-red-200 hover:bg-red-50" data-testid={`outcome-delete-${o.id}`}>
                <Trash2 className="w-3 h-3 mr-1"/> Delete
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="md:col-span-2 p-8 text-center text-sm text-slate-500 border-dashed border-slate-300" data-testid="outcome-empty">
            No outcomes published yet. Click &ldquo;Add outcome&rdquo; to curate your first anonymised success story.
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", textarea = false, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full h-20 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          data-testid={testId}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={testId}
        />
      )}
    </label>
  );
}
